import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";

import { getDatum } from "@accredo/fe-shared/services/formatService.js";
import TabList from "@accredo/fe-shared/components/TabList.jsx";
import { Button } from "@accredo/fe-shared/theme/components";

import { metaForType } from "../constants/documentTypeMeta.js";
import { iconForMimetype } from "./documentFileIcons.js";

/**
 * Mobile card layout for the Kundenportal's Dokument-Tabelle - used instead
 * of the shared DataTable/DataList below the "md" breakpoint, as that list
 * view is reused by every table in the app and a document-specific layout
 * (download action top-right, tighter spacing) would change all of them. See
 * ContractCardList.jsx, which this mirrors.
 *
 * With `selectable`, each card gets a checkbox and a bar above the cards
 * selects/clears the loaded page and offers the zip download of the
 * selection. `rowSelection` is the same { [id]: true } map DataTable uses.
 */
export default function DocumentCardList({
  rows,
  isLoading,
  hasLoaded,
  pageIndex = 0,
  pageSize = 10,
  totalRows = 0,
  onChangePage,
  onRowClick,
  showVertragObjekt = true,
  onVertragClick,
  filterTabs,
  activeFilterTab,
  title,
  documentTypes = [],
  selectable = false,
  rowSelection = {},
  onRowSelectionChange,
  onDownloadSelected,
}) {
  const theme = useTheme();
  const pageCount = Math.ceil(totalRows / pageSize) || 1;
  const selectedCount = rows.filter((doc) => rowSelection[doc.id]).length;
  const allSelected = rows.length > 0 && selectedCount === rows.length;

  const toggleRow = (doc) => {
    const next = { ...rowSelection };
    if (next[doc.id]) {
      delete next[doc.id];
    } else {
      next[doc.id] = true;
    }
    onRowSelectionChange(next);
  };

  const toggleAll = () => {
    onRowSelectionChange(
      allSelected
        ? {}
        : Object.fromEntries(rows.map((doc) => [doc.id, true]))
    );
  };

  if (!hasLoaded && isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ opacity: isLoading ? 0.6 : 1 }}>
      {title && (
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5 }}>
          {title}
        </Typography>
      )}

      {filterTabs && filterTabs.length > 0 && (
        <TabList
          tabs={filterTabs}
          activeTab={activeFilterTab}
          onTabChange={(tabKey) => {
            const tab = filterTabs.find((t) => t.key === tabKey);
            tab?.onClick?.(tabKey);
          }}
          sx={{ mb: 1.5 }}
        />
      )}

      {selectable && rows.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <FormControlLabel
            label="Alle auswählen"
            control={
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={selectedCount > 0 && !allSelected}
                onChange={toggleAll}
              />
            }
          />
          {selectedCount > 0 && (
            <Button
              variant="primary"
              startIcon={<DownloadOutlined />}
              onClick={onDownloadSelected}
            >
              Ausgewählte herunterladen
            </Button>
          )}
        </Box>
      )}

      <Stack spacing={1}>
        {rows.map((doc) => {
          const FileIcon = iconForMimetype(doc.mimetype);

          return (
          <Box
            key={doc.id}
            onClick={onRowClick ? () => onRowClick(doc) : undefined}
            sx={{
              border: `1px solid ${theme.palette.grey[200]}`,
              borderRadius: theme.spacing(1),
              p: 1.5,
              cursor: onRowClick ? "pointer" : "default",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              {selectable && (
                <Checkbox
                  size="small"
                  checked={Boolean(rowSelection[doc.id])}
                  onClick={(event) => event.stopPropagation()}
                  onChange={() => toggleRow(doc)}
                  inputProps={{ "aria-label": `${doc.bezeichnung} auswählen` }}
                  sx={{ p: 0, mt: 0.25 }}
                />
              )}
              <Box sx={{ minWidth: 0, overflow: "hidden", flexGrow: 1 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {doc.bezeichnung}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {metaForType(doc.type, documentTypes).label}
                </Typography>
              </Box>
              <FileIcon
                fontSize="small"
                sx={{ flexShrink: 0, color: theme.palette.text.secondary }}
              />
            </Box>

            {showVertragObjekt && (doc.objekt || doc.vertrags?.length > 0) && (
              <Box sx={{ mt: 1 }}>
                {doc.objekt && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    {doc.objekt.bezeichnung}
                  </Typography>
                )}
                {/* A collective invoice is linked to several Verträge - each
                    gets its own clickable Vertragsnummer rather than picking
                    one, same as the desktop column. */}
                {(doc.vertrags || []).map((vertrag) => (
                  <Typography
                    key={vertrag.id}
                    variant="caption"
                    sx={{
                      display: "block",
                      cursor: "pointer",
                      width: "fit-content",
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      onVertragClick?.(vertrag);
                    }}
                  >
                    {vertrag.vertragsnummer}
                  </Typography>
                ))}
              </Box>
            )}

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              {getDatum(doc.document_date)}
            </Typography>
          </Box>
          );
        })}
      </Stack>

      {pageCount > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={pageCount}
            page={pageIndex + 1}
            onChange={(_, value) =>
              onChangePage?.({ pageIndex: value - 1, pageSize })
            }
            color="primary"
            siblingCount={0}
          />
        </Box>
      )}
    </Box>
  );
}
